<script setup lang="ts">
import { breakpointsTailwind } from "@vueuse/core";
import type { Schemas } from "#shopware";

interface Item {
	id: string;
	label: string | undefined;
	thumbUrl: string;
	price: number | undefined;
	quantity: number;
	type: string;
}

const isModalOpen = ref(false);
const lineItems = ref<Item[]>([]);
const productsAdded = ref<string[]>([]);
const loadingProducts = ref<string[]>([]);

const breakPoints = useBreakpoints(breakpointsTailwind);
const { refreshCart, cart, addProduct, emptyCartMergeState, addProducts } = useCart();

const lineItemsToDo = computed<Item[]>(() => {
	return lineItems.value.filter((item: Item) => {
		return !productsAdded.value.includes(item.id);
	});
});

const lineItemsDone = computed<Item[]>(() => {
	return lineItems.value.filter((item: Item) => {
		return productsAdded.value.includes(item.id);
	});
});

const disableScroll = () => {
	document.body.classList.add("overflow-hidden");
};

const enableScroll = () => {
	document.body.classList.remove("overflow-hidden");
};

const addLineItem = async (item: Item) => {
	loadingProducts.value.push(item.id);

	try {
		await addProduct({
			id: item.id,
			quantity: item.quantity
		});

		productsAdded.value.push(item.id);

		push.success({
			props: {
				isAddToCart: true,
				product: {
					label: item.label,
					thumbUrl: item.thumbUrl
				}
			}
		});
	} catch (error) {
		console.error("Error adding item to the cart", error);
		push.error({
			message:
				"Er is iets misgegaan bij het toevoegen van het product aan je winkelmandje.\n\nKlik hier om het opnieuw te proberen.",
			props: {
				onClick: () => {
					addLineItem(item);
				}
			}
		});
	}

	loadingProducts.value = loadingProducts.value.filter((key) => key !== item.id);
};

const addAllLineItems = async () => {
	loadingProducts.value.push("all");

	// filterout the products that are already added to the cart
	const productsToAdd = lineItems.value.filter((item) => !productsAdded.value.includes(item.id));

	// set all items to loading
	for (const item of productsToAdd) {
		loadingProducts.value.push(item.id);
	}

	try {
		// Add the selected products to the cart
		await addProducts(
			productsToAdd.map((item) => ({ referencedId: item.id, quantity: item.quantity, type: "product" }))
		);

		for (const item of productsToAdd) {
			productsAdded.value.push(item.id);

			push.success({
				props: {
					isAddToCart: true,
					product: {
						label: item.label,
						thumbUrl: item.thumbUrl
					}
				}
			});
		}
	} catch {
		push.error({ message: "Er is iets misgegaan bij het toevoegen van de producten aan je winkelmandje." });
	}

	// remove all loading products
	for (const item of productsToAdd) {
		loadingProducts.value = loadingProducts.value.filter((key) => key !== item.id);
	}

	loadingProducts.value = loadingProducts.value.filter((key) => key !== "all");
};

const openModal = async () => {
	unwatchMergeExtension();
	disableScroll();

	setTimeout(() => {
		emptyCartMergeState();
	}, 5 * 1000);

	isModalOpen.value = true;
};

const closeModal = () => {
	enableScroll();

	emptyCartMergeState();
	isModalOpen.value = false;
};

// watch for usable cart line items to merge with the current cart
const unwatchMergeExtension = watch(
	() => cart?.value?.extensions?.["cart-mergable-line-items"],
	(newValue) => {
		// check if value is a filled array
		if (!Array.isArray(newValue) || newValue.length === 0) {
			return;
		}

		// skip if the lineItems are already handled
		if (lineItems.value.length > 0 || productsAdded.value.length > 0) {
			return;
		}

		// map line items for the modal
		lineItems.value = (newValue || []).map((lineItem: Schemas["LineItem"]) => {
			return {
				id: lineItem.id,
				label: lineItem.label,
				thumbUrl: (lineItem.cover?.thumbnails || []).find((thumb) => thumb.width === 280)?.url,
				price: lineItem.price?.totalPrice,
				quantity: lineItem.quantity,
				type: lineItem.type
			};
		});

		if (lineItems.value.length > 0) {
			openModal();
		}
	}
);

onMounted(() => {
	refreshCart();
});
</script>
<template>
	<KippieDrawer :placement="breakPoints.isGreaterOrEqual('lg') ? 'right' : 'bottom'" :open="isModalOpen">
		<div class="flex flex-col h-full">
			<div class="py-2 px-5 lg:px-8 flex flex-col overflow-hidden">
				<div class="flex flex-col gap-6">
					<div class="flex justify-between items-center">
						<Typography variant="h2"> Oude winkelwagen samenvoegen? </Typography>

						<KippieIconButton icon="close" @click="closeModal" />
					</div>
					<p>
						We hebben een oude winkelwagen gevonden die je kunt samenvoegen met je huidige winkelwagen. Wil
						je dit doen?
					</p>
				</div>

				<div class="flex-1 my-2 overflow-x-hidden overflow-y-auto">
					<template v-for="item in [...lineItemsToDo, ...lineItemsDone]" :key="item.id">
						<div class="bg-white flex gap-6 my-4 items-center">
							<div
								class="bg-yellow rounded-lg aspect-square p-2 flex items-center justify-center w-16 shrink-0 self-start"
							>
								<img
									v-if="item.thumbUrl"
									:src="item.thumbUrl"
									:alt="item.label"
									class="w-full h-full object-contain"
								/>
							</div>
							<div class="flex flex-col grow">
								<h4 class="text-base leading-7 font-bold">
									<span v-if="item.quantity">{{ item.quantity }}&times;</span>
									{{ item.label }}
								</h4>
								<div class="flex flex-col text-xs text-gray">
									<span v-if="item.price">Prijs: € {{ item.price }}</span>
								</div>
							</div>

							<div class="w-[74px]">
								<KippieButton
									class="ml-auto"
									size="small"
									color="white"
									@click="addLineItem(item)"
									:disabled="productsAdded.includes(item.id)"
									:loading="loadingProducts.includes(item.id)"
								>
									<SvgIcon v-if="productsAdded.includes(item.id)" name="check" />
									<SvgIcon v-else name="plus" />
								</KippieButton>
							</div>
						</div>
						<Divider class="last-of-type:hidden" />
					</template>
				</div>
				<div class="flex justify-end space-x-4">
					<KippieButton color="white" @click="closeModal"> Annuleer </KippieButton>
					<KippieButton
						v-if="lineItemsToDo.length > 0"
						@click="addAllLineItems"
						:loading="loadingProducts.includes('all')"
					>
						{{ lineItems.length === 1 ? "Voeg toe" : "Voeg alles toe" }}
					</KippieButton>
					<KippieButton v-else @click="closeModal"> Ga verder </KippieButton>
				</div>
			</div>
		</div>
	</KippieDrawer>
</template>
